import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Edit2, Save, X, Camera, Star, MessageSquare } from 'lucide-react';
import kitchenApi, { resolveImageUrl } from '../services/kitchenApiService';
import { useKitchenAuth } from '../context/KitchenAuthContext';

const DEFAULT_PROFILE = {
  kitchen_name: '', business_name: '', first_name: '', last_name: '',
  email: '', phone: '', contact_person_name: '', contact_person_phone: '',
  address_line1: '', city: '', state: '', postal_code: '', country: 'US',
  store_type: '', food_type: '',
};

export default function SettingsPage() {
  const { user, userRole } = useKitchenAuth();
  const [tab,     setTab]     = useState('profile');
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState(DEFAULT_PROFILE);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState('');
  const [verificationStatus, setVerificationStatus] = useState('not_submitted');
  const [avatarImgError, setAvatarImgError] = useState(false);
  const logoRef = useRef(null);

  const showMsg = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, vRes] = await Promise.all([
        kitchenApi.getCurrentUser(),
        kitchenApi.getKitchenVerification(),
      ]);
      if (uRes.success) {
        const p = uRes.data?.user || uRes.data;
        // Also pull profile_image_url from the kitchen dict (to_dict exposes it)
        const kitchenData = vRes.success ? (vRes.data?.kitchen || {}) : {};
        const merged = {
          ...DEFAULT_PROFILE,
          ...p,
          image_url: p?.image_url || kitchenData?.profile_image_url || null,
        };
        setProfile(merged);
        setForm(merged);
      }
      if (vRes.success) setVerificationStatus(vRes.data?.verification_status || 'not_submitted');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    const res = await kitchenApi.updateKitchenProfile(form);
    setSaving(false);
    if (res.success) {
      setProfile(form);
      setEditing(false);
      showMsg('Profile saved!');
    } else {
      showMsg('Failed to save. Please try again.');
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    showMsg('Uploading…');
    setAvatarImgError(false);
    const res = await kitchenApi.uploadKitchenImage('logo', file);
    if (res.success) {
      const newUrl = res.data?.image_url || res.data?.url;
      if (newUrl) {
        setProfile(p => ({ ...p, image_url: newUrl }));
      }
      showMsg('Profile image updated!');
      load(); // re-fetch to confirm persisted state
    } else {
      showMsg(res.error || 'Image upload failed. Please try again.');
    }
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const VERIFICATION_BADGE = {
    not_submitted: { label: 'Not Submitted', color: '#94A3B8', bg: '#F1F5F9' },
    pending:       { label: 'Under Review',  color: '#D97706', bg: '#FFFBEB' },
    approved:      { label: 'Verified ✓',   color: '#059669', bg: '#ECFDF5' },
    rejected:      { label: 'Action Needed', color: '#DC2626', bg: '#FEF2F2' },
  };
  const vBadge = VERIFICATION_BADGE[verificationStatus] || VERIFICATION_BADGE.not_submitted;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Settings</h1>
          <p className="k-page-sub">Manage your kitchen profile and preferences</p>
        </div>
        <button className="k-btn-icon" onClick={() => load()} title="Refresh"><RefreshCw size={16} /></button>
      </div>

      {/* Tabs */}
      <div className="k-tabs" style={{ marginBottom: 20, maxWidth: 400 }}>
        <button className={`k-tab${tab === 'profile' ? ' active' : ''}`} onClick={() => setTab('profile')}>Profile</button>
        <button className={`k-tab${tab === 'account' ? ' active' : ''}`} onClick={() => setTab('account')}>Account</button>
        <button className={`k-tab${tab === 'verification' ? ' active' : ''}`} onClick={() => setTab('verification')}>Verification</button>
      </div>

      {loading ? (
        <div className="k-loading-center"><div className="k-spinner" /></div>
      ) : (
        <>
          {/* ── Profile Tab ── */}
          {tab === 'profile' && (
            <div>
              {/* Avatar */}
              <div className="k-card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <div style={{ position: 'relative' }}>
                    <div className="k-avatar" style={{ width: 72, height: 72, fontSize: 24 }}>
                      {profile.image_url && !avatarImgError ? (
                        <img
                          src={resolveImageUrl(profile.image_url)}
                          alt="profile"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                          onError={() => setAvatarImgError(true)}
                        />
                      ) : (
                        <span style={{ userSelect: 'none' }}>
                          {(profile.kitchen_name || profile.first_name || 'K').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => logoRef.current?.click()}
                      style={{
                        position: 'absolute', bottom: -4, right: -4,
                        width: 26, height: 26, borderRadius: '50%',
                        background: 'var(--k-primary)', border: '2px solid #fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Camera size={12} color="#fff" />
                    </button>
                    <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoChange} />
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{profile.kitchen_name || profile.first_name}</div>
                    <div style={{ fontSize: 13, color: 'var(--k-text-sub)', marginTop: 2 }}>{profile.email}</div>
                    <div style={{ marginTop: 6 }}>
                      <span style={{ background: vBadge.bg, color: vBadge.color, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999 }}>
                        {vBadge.label}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    {!editing ? (
                      <button className="k-btn k-btn-outline" onClick={() => { setForm({ ...profile }); setEditing(true); }}>
                        <Edit2 size={14} /> Edit Profile
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="k-btn k-btn-ghost" onClick={() => setEditing(false)}><X size={14} /> Cancel</button>
                        <button className="k-btn k-btn-primary" onClick={handleSave} disabled={saving}>
                          {saving ? <span className="k-spinner k-spinner-sm" style={{ borderTopColor: '#fff' }} /> : <><Save size={14} /> Save</>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Profile fields */}
              <div className="k-card">
                <div className="k-card-title" style={{ marginBottom: 16 }}>Kitchen Information</div>
                <div className="k-grid-2">
                  {[
                    { k: 'kitchen_name',   l: 'Kitchen Name' },
                    { k: 'email',          l: 'Email' },
                    { k: 'phone',          l: 'Phone' },
                    { k: 'contact_person_name', l: 'Contact Person' },
                    { k: 'contact_person_phone', l: 'Contact Phone' },
                    { k: 'store_type',     l: 'Store Type' },
                  ].map(({ k, l }) => (
                    <div key={k} className="k-form-group">
                      <label className="k-label">{l}</label>
                      {editing ? (
                        <input className="k-input" value={form[k] || ''} onChange={e => setF(k, e.target.value)} />
                      ) : (
                        <div style={{ fontSize: 14, color: 'var(--k-text)', padding: '10px 0', borderBottom: '1px solid var(--k-border-light)' }}>
                          {profile[k] || <span style={{ color: 'var(--k-text-muted)' }}>Not set</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="k-card-title" style={{ margin: '20px 0 16px' }}>Address</div>
                <div className="k-grid-2">
                  {[
                    { k: 'address_line1', l: 'Street Address' },
                    { k: 'city',          l: 'City' },
                    { k: 'state',         l: 'State' },
                    { k: 'postal_code',   l: 'Zip Code' },
                  ].map(({ k, l }) => (
                    <div key={k} className="k-form-group">
                      <label className="k-label">{l}</label>
                      {editing ? (
                        <input className="k-input" value={form[k] || ''} onChange={e => setF(k, e.target.value)} />
                      ) : (
                        <div style={{ fontSize: 14, color: 'var(--k-text)', padding: '10px 0', borderBottom: '1px solid var(--k-border-light)' }}>
                          {profile[k] || <span style={{ color: 'var(--k-text-muted)' }}>Not set</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Account Tab ── */}
          {tab === 'account' && (
            <div className="k-card">
              <div className="k-card-title" style={{ marginBottom: 16 }}>Account Information</div>
              {[
                { label: 'User ID',     value: profile.user_id || profile.kitchen_id || '—' },
                { label: 'Username',    value: profile.username || '—' },
                { label: 'Full Name',   value: profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || '—' },
                { label: 'Email',       value: profile.email || '—' },
                { label: 'Phone',       value: profile.phone || '—' },
                { label: 'Role',        value: userRole || profile.role || '—' },
                { label: 'Member Since', value: profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '—' },
                { label: 'Email Verified', value: profile.is_email_verified ? '✓ Verified' : '✗ Not verified' },
              ].map(({ label, value }) => (
                <div key={label} className="k-flex-between" style={{ padding: '12px 0', borderBottom: '1px solid var(--k-border-light)', fontSize: 14 }}>
                  <span style={{ color: 'var(--k-text-sub)' }}>{label}</span>
                  <span style={{ fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Verification Tab ── */}
          {tab === 'verification' && (
            <div className="k-card">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🛡️</div>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Kitchen Verification</div>
                <div style={{ marginBottom: 16 }}>
                  <span style={{ background: vBadge.bg, color: vBadge.color, fontSize: 14, fontWeight: 700, padding: '6px 16px', borderRadius: 999 }}>
                    {vBadge.label}
                  </span>
                </div>
                <p style={{ fontSize: 14, color: 'var(--k-text-sub)', maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>
                  {verificationStatus === 'approved'
                    ? 'Your kitchen has been verified. You have full access to all features.'
                    : verificationStatus === 'pending'
                    ? 'Your verification is under review. We\'ll notify you once reviewed.'
                    : verificationStatus === 'rejected'
                    ? 'Your verification was rejected. Please update your documents and resubmit.'
                    : 'Submit your business documents to get your kitchen verified.'}
                </p>
                {verificationStatus !== 'approved' && (
                  <button className="k-btn k-btn-primary" style={{ marginTop: 20 }}>
                    {verificationStatus === 'rejected' ? 'Resubmit Documents' : 'Submit for Verification'}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Toast */}
      {toast && (
        <div className="k-toast-container">
          <div className="k-toast success">✓ {toast}</div>
        </div>
      )}
    </div>
  );
}
